import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, User } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating, onRate, interactive = false, size = 'w-5 h-5' }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate?.(star)}
          className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
        >
          <Star
            className={`${size} ${star <= rating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewSection({ listingId, ownerId }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasReviewed, setHasReviewed] = useState(false);

  React.useEffect(() => {
    fetchReviews();
  }, [listingId]);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/${listingId}`);
      setReviews(data);
      if (user) {
        setHasReviewed(data.some(r => r.user_id === user._id));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newRating || !newComment.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/reviews`, {
        listing_id: listingId,
        rating: newRating,
        comment: newComment
      });
      setNewRating(0);
      setNewComment('');
      fetchReviews();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error');
    } finally { setSubmitting(false); }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const isOwner = user && user._id === ownerId;
  const canReview = user && !isOwner && !hasReviewed;

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="review-section">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-outfit font-semibold text-lg text-[#121212]">
          {language === 'lv' ? 'Atsauksmes' : 'Reviews'}
        </h3>
        {reviews.length > 0 && (
          <div className="flex items-center gap-2">
            <StarRating rating={Math.round(avgRating)} />
            <span className="font-semibold text-[#121212]">{avgRating}</span>
            <span className="text-sm text-[#5C5C5C]">({reviews.length})</span>
          </div>
        )}
      </div>

      {/* Write Review */}
      {canReview && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-[#F3F2EE] rounded-xl">
          {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Jūsu vērtējums:' : 'Your rating:'}</span>
            <StarRating rating={newRating} onRate={setNewRating} interactive size="w-6 h-6" />
          </div>
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={language === 'lv' ? 'Jūsu atsauksme...' : 'Your review...'}
            rows={3}
            className="mb-3 resize-none bg-white"
            data-testid="review-comment-input"
          />
          <Button
            type="submit"
            disabled={submitting || !newRating || newComment.trim().length < 3}
            className="bg-[#D97736] hover:bg-[#C06528] text-white"
            data-testid="submit-review-btn"
          >
            {submitting ? (language === 'lv' ? 'Sūta...' : 'Sending...') : (language === 'lv' ? 'Nosūtīt atsauksmi' : 'Submit Review')}
          </Button>
        </form>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">{[...Array(2)].map((_, i) => <div key={i} className="h-20 bg-[#F3F2EE] rounded-xl animate-pulse" />)}</div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map(review => (
            <div key={review.id} className="flex gap-4" data-testid={`review-${review.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#4A6B53] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {review.user_name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-[#121212] text-sm">{review.user_name}</span>
                  <StarRating rating={review.rating} size="w-3.5 h-3.5" />
                  <span className="text-xs text-[#5C5C5C]">
                    {new Date(review.created_at).toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US')}
                  </span>
                </div>
                <p className="text-sm text-[#5C5C5C]">{review.comment}</p>
                {Array.isArray(review.images) && review.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {review.images.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={resolveAssetUrl(url)}
                          alt=""
                          className="w-20 h-20 object-cover rounded-lg border border-[#E5E4E0] hover:border-[#D97736] transition-colors"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-[#5C5C5C] py-4">
          {language === 'lv' ? 'Vēl nav atsauksmju' : 'No reviews yet'}
        </p>
      )}
    </div>
  );
}

export { StarRating };
